import type {
  AdSizeForecasting,
  DataTypeForecasting,
  SortingKeysForecasting,
  USCityForecasting,
} from '@openx/types/dealForecasting.ts';
import i18n from 'i18next';
import cloneDeep from 'lodash/cloneDeep';
import type { Dispatch } from 'react';

import {
  type FiltersAction,
  FiltersActionType,
  SortDirection,
  type TableState,
  UnforecastedTargetingSectionTab,
} from '../types.ts';

export const getPlaceholderText = (type: UnforecastedTargetingSectionTab) => {
  switch (type) {
    case UnforecastedTargetingSectionTab.AdSize:
      return i18n.t('Search Ad Size') || 'Search Ad Size';
    case UnforecastedTargetingSectionTab.USCity:
      return i18n.t('Search US City or State') || 'Search US City or State';
    default:
      return 'Search';
  }
};

export const displayData = (key: SortingKeysForecasting, data: DataTypeForecasting): string => {
  if (key === 'fraction') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 4,
      style: 'percent',
    }).format(data[key]);
  }

  if (key === 'city' || key === 'state') {
    return displayCapitalizedCityState(key, data);
  }

  return data[key];
};

export const removeRecordsWithNullValues = (data: DataTypeForecasting[]) => {
  return data.filter(record => Object.values(record).every(value => value !== null));
};

export const displayCapitalizedCityState = (key: SortingKeysForecasting, data: DataTypeForecasting) => {
  return data?.[key]
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const processType = (type: UnforecastedTargetingSectionTab) => {
  const types = {
    [UnforecastedTargetingSectionTab.AdSize]: {
      ad_size: i18n.t('Ad Size'),
      fraction: i18n.t('Percent of Bids'),
    },
    [UnforecastedTargetingSectionTab.USCity]: {
      city: i18n.t('City'),
      fraction: i18n.t('Percent of Bids'),
      state: i18n.t('State'),
    },
    [UnforecastedTargetingSectionTab.VideoDuration]: {
      fraction: i18n.t('Percent of Bids'),
      maxduration: i18n.t('Max Duration'),
      minduration: i18n.t('Min Duration'),
    },
  };

  return types[type] || i18n.t('Unknown Type');
};

export const isAdSize = (record: TableState['data']): record is AdSizeForecasting[] => {
  return (record[0] as AdSizeForecasting)?.ad_size !== undefined;
};

export const isUSCity = (record: TableState['data']): record is USCityForecasting[] => {
  return (record[0] as USCityForecasting)?.city !== undefined && (record[0] as USCityForecasting)?.state !== undefined;
};

export const filterData = (
  data: TableState['data'],
  phrase: string,
  type: UnforecastedTargetingSectionTab
): TableState['data'] => {
  if (!phrase || type === UnforecastedTargetingSectionTab.VideoDuration || data.length === 0) {
    return data;
  }

  if (type === UnforecastedTargetingSectionTab.AdSize && isAdSize(data)) {
    return data.filter(record => record?.ad_size.toLowerCase().includes(phrase.toLowerCase()));
  }

  if (type === UnforecastedTargetingSectionTab.USCity && isUSCity(data)) {
    return data.filter(
      record =>
        record.city.toLowerCase().includes(phrase.toLowerCase()) ||
        record.state.toLowerCase().includes(phrase.toLowerCase())
    );
  }

  return data;
};

export const getSortValue = (aVal: string | number, bVal: string | number, direction: number) => {
  if (aVal < bVal) {
    return direction === 1 ? -1 : 1;
  }

  if (aVal > bVal) {
    return direction === 1 ? 1 : -1;
  }

  return 0;
};

const sortDirectionFromNumber = (direction: number): SortDirection =>
  direction > 0 ? SortDirection.ASC : SortDirection.DESC;

export const sortData = (
  stateData: TableState['data'],
  sortColumn?: SortingKeysForecasting,
  sortSettings?: TableState['filters']['sort'],
  sortDirection: SortDirection = SortDirection.DESC,
  shouldKeepSortOrder = false
) => {
  const data = cloneDeep(stateData);

  const { dir } = sortSettings || { column: 'fraction', dir: SortDirection.ASC };
  const updatedDirection = shouldKeepSortOrder ? dir : sortDirection;

  return data.sort((a: DataTypeForecasting, b: DataTypeForecasting) => {
    if (sortColumn) {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (typeof a[sortColumn] === 'string' && typeof b[sortColumn] === 'string') {
        return getSortValue(aVal.toLowerCase(), bVal.toLowerCase(), updatedDirection);
      }

      return getSortValue(aVal, bVal, updatedDirection);
    }

    return 0;
  });
};

export const getInitSortingColumn = (data: TableState['data']): SortingKeysForecasting => {
  if (!data || data.length === 0) {
    return '' as SortingKeysForecasting;
  }

  const keys = Object.keys(data[0]);

  if (keys.includes('fraction')) {
    return 'fraction';
  }

  return (keys[0] || '') as SortingKeysForecasting;
};

export const dispatchPhraseChange = (
  phrase: string,
  initState: TableState,
  type: UnforecastedTargetingSectionTab,
  dispatch: Dispatch<FiltersAction>
) => {
  dispatch({ initState, phrase, tableType: type, type: FiltersActionType.Phrase });
};

export const dispatchChangeSortingDirection = (
  key: SortingKeysForecasting,
  state: TableState,
  dispatch: Dispatch<FiltersAction>
) => {
  const sortDirection =
    state.filters.sort.column === key ? sortDirectionFromNumber(state.filters.sort.dir * -1) : SortDirection.DESC;

  dispatch({ sortColumn: key, sortDirection, type: FiltersActionType.ChangeSortingDirection });
};

export const dispatchInitialData = (
  state: TableState,
  sort: TableState['filters']['sort'],
  tableType: UnforecastedTargetingSectionTab,
  dispatch: Dispatch<FiltersAction>
) => {
  const applySortToInitState = { ...state, filters: { ...state.filters, sort } };

  dispatch({ initState: applySortToInitState, tableType, type: FiltersActionType.ResetState });
};

export const reducer: React.Reducer<TableState, FiltersAction> = (
  state: TableState,
  action: FiltersAction
): TableState => {
  const { type, phrase, tableType, sortColumn, initState, sortDirection } = action;

  switch (type) {
    case FiltersActionType.Phrase:
      return {
        ...state,
        data: sortData(
          filterData(initState?.data || state.data, phrase || '', tableType || UnforecastedTargetingSectionTab.AdSize),
          state.filters.sort.column,
          state.filters.sort,
          sortDirectionFromNumber(state.filters.sort.dir),
          true
        ),
        filters: {
          ...state.filters,
          phrase: phrase || '',
        },
      };

    case FiltersActionType.ChangeSortingDirection: {
      return {
        ...state,
        data: sortData(state.data, sortColumn, state.filters.sort, sortDirection),
        filters: {
          ...state.filters,
          sort: {
            column: sortColumn || state.filters.sort.column,
            dir: sortDirection || state.filters.sort.dir,
          },
        },
      };
    }

    case FiltersActionType.ResetState:
      return {
        data: sortData(
          action.initState?.data || state.data,
          state.filters.sort.column,
          state.filters.sort,
          sortDirectionFromNumber(state.filters.sort.dir)
        ),
        filters: action.initState?.filters || state.filters,
        type: action.tableType || state.type,
      };

    default:
      return state;
  }
};
